/*
  TheTCGShack: out-of-stock products at the back of every collection (owner, 28 Oct 2026: "fix collections so all
  out of stock is at the back of the list ... right now they are scattered").
  Shopify has no "sold out last" sort, so each page of the grid is rebuilt from two lists of the same collection,
  sort and filters: in stock first (filter.v.availability=1), then out of stock (filter.v.availability=0).
  main-collection-product-grid passes both counts in #product-grid[data-tcgs-oos]; the total is unchanged, so the
  page numbers stay right. Nothing happens when the shopper picks an availability filter, or when a page has nothing
  to move. Runs again whenever filters, sorting or pagination redraw #ProductGridContainer.
*/
(() => {
  if (window.tcgsOosLast) return;
  window.tcgsOosLast = true;

  const TIMEOUT = 4000;
  const cache = new Map();

  const fetchHtml = (url) => {
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      }).catch((e) => { cache.delete(url); throw e; }));
    }
    return cache.get(url);
  };

  const gridItems = (html) =>
    [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('#product-grid > li.grid__item')];

  // While a page is rebuilt the grid is dimmed under the theme's own loading overlay (no blank area).
  const busy = (grid, on) => {
    const box = grid.closest('.collection');
    if (box) box.classList.toggle('loading', on);
  };
  const reveal = (grid) => { grid.classList.remove('tcgs-oos-pending'); busy(grid, false); };

  const segmentUrl = (sectionId, availability, page) => {
    const params = new URLSearchParams(window.location.search);
    ['page', 'section_id', 'filter.v.availability'].forEach((key) => params.delete(key));
    params.set('filter.v.availability', availability);
    params.set('page', page);
    params.set('section_id', sectionId);
    return `${window.location.pathname}?${params.toString()}`;
  };

  async function reorder(grid) {
    if (!grid || grid.dataset.tcgsOosDone) return;
    grid.dataset.tcgsOosDone = '1';

    let cfg = null;
    try { cfg = JSON.parse(grid.dataset.tcgsOos || 'null'); } catch (e) { cfg = null; }
    if (!cfg || !cfg.on) { reveal(grid); return; }
    busy(grid, true);

    const per = cfg.per, page = cfg.page, inStock = cfg.in, outOfStock = cfg.out;
    const start = per * (page - 1);
    const end = Math.min(per * page, inStock + outOfStock);
    if (start >= end) { reveal(grid); return; }

    // Which slices of the two lists make up this page.
    const parts = [];
    if (start < inStock) {
      parts.push({ availability: 1, page, from: 0, to: Math.min(end, inStock) - start });
    }
    if (end > inStock) {
      const oStart = Math.max(start, inStock) - inStock;
      const oEnd = end - inStock;
      const first = Math.floor(oStart / per) + 1;
      const last = Math.floor((oEnd - 1) / per) + 1;
      for (let q = first; q <= last; q += 1) {
        parts.push({
          availability: 0,
          page: q,
          from: Math.max(oStart - (q - 1) * per, 0),
          to: Math.min(oEnd - (q - 1) * per, per),
        });
      }
    }

    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; reveal(grid); }, TIMEOUT);
    try {
      const htmls = await Promise.all(parts.map((p) => fetchHtml(segmentUrl(grid.dataset.id, p.availability, p.page))));
      if (timedOut || !grid.isConnected) return;
      const items = [];
      htmls.forEach((html, i) => items.push(...gridItems(html).slice(parts[i].from, parts[i].to)));
      if (items.length) {
        grid.replaceChildren(...items.map((li) => document.importNode(li, true)));
        const list = grid.closest('appear-animate-list');
        if (list && typeof list.reset === 'function') list.reset();
      }
    } catch (e) {
      console.warn('tcgs-oos-last: kept the original order', e);
    } finally {
      clearTimeout(timer);
      reveal(grid);
    }
  }

  const scan = () => document.querySelectorAll('#product-grid[data-tcgs-oos]').forEach(reorder);

  // Sorting and filtering redraw the grid through facets.js, which updates the URL right after it starts its own
  // request. Starting this page's in-stock request at that moment runs the two side by side (28 Oct 2026: sorting
  // took 4-6 seconds when they ran one after the other).
  const prefetch = () => {
    const grid = document.querySelector('#product-grid[data-tcgs-oos]');
    if (!grid) return;
    let cfg = null;
    try { cfg = JSON.parse(grid.dataset.tcgsOos || 'null'); } catch (e) { cfg = null; }
    if (!cfg || !cfg.on) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has('filter.v.availability')) return;
    const page = parseInt(params.get('page') || '1', 10) || 1;
    fetchHtml(segmentUrl(grid.dataset.id, 1, page)).catch(() => {});
  };
  ['pushState', 'replaceState'].forEach((name) => {
    const original = history[name];
    history[name] = function (...args) {
      const result = original.apply(this, args);
      try { prefetch(); } catch (e) { /* never block navigation */ }
      return result;
    };
  });

  // The script tag sits right after the grid, so the two fetches start while the rest of the page is still loading.
  scan();
  const watch = () => {
    const container = document.getElementById('ProductGridContainer');
    if (container) new MutationObserver(scan).observe(container, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { scan(); watch(); });
  else watch();
})();
